import { Plane, Wrench, Cpu, HardHat } from "lucide-react";
import Helicopter from "./HelicopterIcon";
import { CATEGORIES } from "./data";

const ICONS = { Plane, Wrench, Cpu, Helicopter };

const SCROLL_MARGIN = { scrollMarginTop: "240px" };

function CategoryCard({ cat, t }) {
  const Icon = ICONS[cat.icon];
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`category-${cat.id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${cat.color}15`, border: `1px solid ${cat.color}30` }}
        >
          <Icon className="w-5 h-5" style={{ color: cat.color }} />
        </div>
        <div className="min-w-0">
          <p className="font-['Outfit'] font-extrabold text-lg text-[#0A1628] leading-tight">{`Cat. ${cat.id}`}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: cat.color }}>
            {t(`coursesPage.subs.${cat.subKey}`)}
          </p>
        </div>
      </div>
      <div className="mt-auto pt-3 border-t border-slate-100">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
          {t("coursesPage.modulesLabel")} · {cat.modules.length}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {cat.modules.map((m) => (
            <span
              key={m}
              className="text-[11px] font-bold px-2 py-0.5 rounded-md"
              style={{ backgroundColor: `${cat.color}12`, color: cat.color }}
            >
              {m}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function GroupHeader({ color, title, desc }) {
  return (
    <div className="border-l-4 pl-4 mb-5" style={{ borderColor: color }}>
      <h4 className="font-['Outfit'] text-xl md:text-2xl font-extrabold text-[#0A1628] mb-1.5">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed max-w-4xl">{desc}</p>
    </div>
  );
}

export default function CategoriesSection({ t }) {
  const groupA  = CATEGORIES.filter((c) => c.group === "A");
  const groupB1 = CATEGORIES.filter((c) => c.group === "B1");
  const groupB2 = CATEGORIES.filter((c) => c.group === "B2");

  return (
    <div className="mt-14">
      <div className="text-center mb-12">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00A8FF] mb-2">
          EASA Part 66 — Appendix I
        </p>
        <h3 className="font-['Outfit'] text-2xl md:text-3xl font-extrabold tracking-tight text-[#0A1628] mb-3">
          {t("coursesPage.catsTitle")}
        </h3>
        <p className="text-sm text-slate-500 max-w-2xl mx-auto">{t("coursesPage.catsSubtitle")}</p>
      </div>

      {/* Unlicensed Mechanic — Foundation card */}
      <div id="g-none" className="mb-12" style={SCROLL_MARGIN}>
        <div className="bg-gradient-to-br from-[#0A1628] to-[#1A2D4A] rounded-2xl p-6 md:p-8 border border-slate-700/50 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#00A8FF]/10 blur-3xl rounded-full" />
          <div className="relative flex flex-col md:flex-row gap-6 items-start">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 bg-[#00A8FF]/15 border border-[#00A8FF]/30">
              <HardHat className="w-8 h-8 text-[#00A8FF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2">
                Step 0 · Basic Experience
              </p>
              <h4 className="font-['Outfit'] text-xl md:text-2xl font-extrabold text-white mb-3 leading-tight">
                {t("coursesPage.groupM.title")}
              </h4>
              <p className="text-sm md:text-[15px] text-slate-300 leading-relaxed">
                {t("coursesPage.groupM.desc")}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-[#00A8FF]/15 text-[#00A8FF] border border-[#00A8FF]/30">+ Cat. A</span>
                <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">+ Cat. B1</span>
                <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/50">On-the-Job Training (OJT)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Group A */}
      <div id="g-a" className="mb-12" style={SCROLL_MARGIN}>
        <GroupHeader color="#00A8FF" title={t("coursesPage.groupA.title")} desc={t("coursesPage.groupA.desc")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {groupA.map((cat) => <CategoryCard key={cat.id} cat={cat} t={t} />)}
        </div>
      </div>

      {/* Group B1 */}
      <div id="g-b1" className="mb-12" style={SCROLL_MARGIN}>
        <GroupHeader color="#F59E0B" title={t("coursesPage.groupB1.title")} desc={t("coursesPage.groupB1.desc")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {groupB1.map((cat) => <CategoryCard key={cat.id} cat={cat} t={t} />)}
        </div>
      </div>

      {/* Group B2 */}
      <div id="g-b2" className="mb-4" style={SCROLL_MARGIN}>
        <GroupHeader color="#10B981" title={t("coursesPage.groupB2.title")} desc={t("coursesPage.groupB2.desc")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {groupB2.map((cat) => <CategoryCard key={cat.id} cat={cat} t={t} />)}
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-xs text-slate-500 italic">{t("coursesPage.source")}</p>
      </div>
    </div>
  );
}
